/**
 * DashboardSVGs.tsx
 *
 * Reusable SVG components for the Dashboard page.
 * These components keep the JSX clean and make SVGs easier to maintain.
 * You can use this pattern for other SVG-heavy pages.
 */

import React from 'react';

/**
 * UserProfileIcon - Shows a user silhouette icon with circle head and body
 * Used in dashboard title area to represent the user
 */
export const UserProfileIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
	<svg
		width="800px"
		height="800px"
		viewBox="0 0 32 32"
		xmlns="http://www.w3.org/2000/svg"
		fill="#000000"
		{...props}
	>
		<g id="SVGRepo_bgCarrier" strokeWidth="0" />
		<g
			id="SVGRepo_tracerCarrier"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<g id="SVGRepo_iconCarrier">
			<title>User Profile Icon</title>
			<g id="fill">
				<circle className="cls-1" cx="16" cy="7.48" r="5.48" />
				<path
					className="cls-2"
					d="M23.54,30H8.46a4.8,4.8,0,0,1-4.8-4.8h0A10.29,10.29,0,0,1,13.94,14.92h4.12A10.29,10.29,0,0,1,28.34,25.2h0A4.8,4.8,0,0,1,23.54,30Z"
				/>
			</g>
		</g>
	</svg>
);

/**
 * CalendarIcon - Shows a calendar with pink/magenta styling
 * Used in dashboard title area to display the current date
 */
export const CalendarIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
	<svg
		width="800px"
		height="800px"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		{...props}
	>
		<g id="SVGRepo_bgCarrier" strokeWidth="0" />
		<g
			id="SVGRepo_tracerCarrier"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<g id="SVGRepo_iconCarrier">
			<path
				d="M6.94028 2C7.35614 2 7.69326 2.32421 7.69326 2.72414V4.18487C8.36117 4.17241 9.10983 4.17241 9.95219 4.17241H13.9681C14.8104 4.17241 15.5591 4.17241 16.227 4.18487V2.72414C16.227 2.32421 16.5641 2 16.98 2C17.3958 2 17.733 2.32421 17.733 2.72414V4.24894C19.178 4.36022 20.1267 4.63333 20.8236 5.30359C21.5206 5.97385 21.8046 6.88616 21.9203 8.27586L22 9H2.92456H2V8.27586C2.11571 6.88616 2.3997 5.97385 3.09665 5.30359C3.79361 4.63333 4.74226 4.36022 6.1873 4.24894V2.72414C6.1873 2.32421 6.52442 2 6.94028 2Z"
				fill="#ff80d7"
			/>
			<path
				opacity="0.5"
				d="M21.9995 14.0001V12.0001C21.9995 11.161 21.9963 9.66527 21.9834 9H2.00917C1.99626 9.66527 1.99953 11.161 1.99953 12.0001V14.0001C1.99953 17.7713 1.99953 19.6569 3.1711 20.8285C4.34267 22.0001 6.22829 22.0001 9.99953 22.0001H13.9995C17.7708 22.0001 19.6564 22.0001 20.828 20.8285C21.9995 19.6569 21.9995 17.7713 21.9995 14.0001Z"
				fill="#ff80d7"
			/>
			<path
				d="M18 17C18 17.5523 17.5523 18 17 18C16.4477 18 16 17.5523 16 17C16 16.4477 16.4477 16 17 16C17.5523 16 18 16.4477 18 17Z"
				fill="#ff80d7"
			/>
			<path
				d="M18 13C18 13.5523 17.5523 14 17 14C16.4477 14 16 13.5523 16 13C16 12.4477 16.4477 12 17 12C17.5523 12 18 12.4477 18 13Z"
				fill="#ff80d7"
			/>
			<path
				d="M13 17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17C11 16.4477 11.4477 16 12 16C12.5523 16 13 16.4477 13 17Z"
				fill="#ff80d7"
			/>
			<path
				d="M13 13C13 13.5523 12.5523 14 12 14C11.4477 14 11 13.5523 11 13C11 12.4477 11.4477 12 12 12C12.5523 12 13 12.4477 13 13Z"
				fill="#ff80d7"
			/>
			<path
				d="M8 17C8 17.5523 7.55228 18 7 18C6.44772 18 6 17.5523 6 17C6 16.4477 6.44772 16 7 16C7.55228 16 8 16.4477 8 17Z"
				fill="#ff80d7"
			/>
			<path
				d="M8 13C8 13.5523 7.55228 14 7 14C6.44772 14 6 13.5523 6 13C6 12.4477 6.44772 12 7 12C7.55228 12 8 12.4477 8 13Z"
				fill="#ff80d7"
			/>
		</g>
	</svg>
);

/**
 * ExternalLinkIcon - Shows an external link icon (arrow pointing out)
 * Used in links that open in a new tab
 */
export const ExternalLinkIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
	<svg
		width="20px"
		height="20px"
		viewBox="0 0 24 24"
		xmlns="http://www.w3.org/2000/svg"
		fill="none"
		{...props}
	>
		<path
			stroke="#000000"
			strokeWidth="2"
			d="M12 6H7a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-5m-6 0 7.5-7.5M15 3h6v6"
		/>
	</svg>
);

/**
 * SaveMoneyPig - Shows the Save Money Pig mascot
 * Large decorative SVG used in dashboard förmånsbutik section
 */
export const SaveMoneyPig: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="217.82 219.78 380.69 389.24"
		{...props}
	>
		<g className="cls-4">
			<g id="Lager_1" data-name="Lager 1">
				<g>
					<g className="cls-6">
						<path
							className="cls-3"
							d="M545.85,459.65c0-72.73-67.26-131.7-150.23-131.7-23.98,0-46.64,4.92-66.75,13.69-7.8,3.39-16.51,4.14-24.77,2.11l-32.2-7.91c-3.76-.92-7.02,2.74-5.67,6.37l9.03,24.25c2.25,6.05,1.14,12.81-2.88,17.86-10.04,12.6-17.63,26.82-22.17,42.12-3.22,10.88-13.14,18.4-24.47,18.4-4.56,0-8.18,3.84-7.9,8.39l2.76,45.46c.5,8.27,7.36,14.73,15.65,14.73,12.8,0,24.83,6.14,32.32,16.52,8.42,11.67,18.86,22.1,30.91,30.9,12.47,9.11,21,22.63,24.23,37.74l1.56,7.29c.39,1.83,2.02,3.15,3.89,3.15h34.31c2.2,0,3.98-1.79,3.98-3.98v-11.78c0-2.2,1.95-3.9,4.13-3.59,7.82,1.11,15.85,1.68,24.03,1.68s16.43-.59,24.35-1.73c2.19-.31,4.14,1.38,4.14,3.59v11.82c0,2.2,1.79,3.98,3.98,3.98h34.31c1.88,0,3.51-1.31,3.9-3.16l1.51-7.06c3.31-15.52,12.25-29.22,24.98-38.7,32.47-24.15,53.07-60.2,53.07-100.46Z"
						/>
					</g>
					<g className="cls-6">
						<path
							className="cls-2"
							d="M437.88,351.07c-10.11-2.72-24.09-4.41-39.52-4.41s-29.41,1.68-39.52,4.41"
						/>
					</g>
					<g className="cls-6">
						<path
							className="cls-2"
							d="M592.26,427.44s-19.48,8.57-35.59.26c-12.83-6.62-13.16-29.38,2.25-31.43,29.18-3.9,21.47,55.07-29.1,42.25"
						/>
					</g>
					<g className="cls-6">
						<path
							className="cls-5"
							d="M398.35,335.62c31.85,0,57.67-25.82,57.67-57.67s-25.82-57.67-57.67-57.67-57.67,25.82-57.67,57.67,25.82,57.67,57.67,57.67Z"
						/>
					</g>
					<g className="cls-6">
						<path
							className="cls-1"
							d="M424.86,299.51h-53.01l-2.35-43.12,15.99,22.17,12.86-22.17,12.86,22.17,15.99-22.17-2.35,43.12Z"
						/>
					</g>
				</g>
			</g>
		</g>
	</svg>
);
